import { observer } from 'mobx-react-lite'
import { useCallback, useId } from 'react'
import { PersonalInfoViewModel } from '@/features/account/PersonalInfoView/PersonalInfoViewModel'
import { useActivation, useScopedViewModel } from '@/hooks/view-model-hooks'
import { useRootStore } from '@/contexts/root-store-context'
import {
  CardButton,
  CardItemContent,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { TermsAcceptancesListViewModel } from '@/features/account/terms/TermsAcceptancesList/TermsAcceptancesListViewModel'
import { Section7216AcceptancesListViewModel } from '@/features/account/section-7216/Section7216AcceptancesList/Section7216AcceptancesListViewModel'
import { EditProfileDialogContainer } from '@/features/account/PersonalInfoView/EditProfileDialog/EditProfileDialogContainer'
import { EditProfileViewModel } from '@/features/account/PersonalInfoView/EditProfileDialog/EditProfileViewModel'
import { useDocumentTitle } from '@/hooks/document-title'
import { Section7216AcceptancesListContainer } from '@/features/account/section-7216/Section7216AcceptancesList/Section7216AcceptancesListContainer'
import { TermsAcceptancesListContainer } from '@/features/account/terms/TermsAcceptancesList/TermsAcceptancesListContainer'
import {
  PersonalInfoView,
  ProfilePanel,
  Section7216AuthorizationsSection,
  TermsOfUseAgreementsSection,
} from './index'

export default observer(function PersonalInfoViewContainer() {
  const termsHeadlineId = useId()
  const section7216HeadlineId = useId()

  const vm = useScopedViewModel(({ iam, account, shell }, needs) => {
    const { internal } = needs({ internal: account.internal() })
    return new PersonalInfoViewModel(
      iam.getCurrentWorkspace,
      iam.getCurrentUser,
      iam.getCurrentMember,
      iam.workspaceMemberStore,
      shell.newSnackbar,
      new TermsAcceptancesListViewModel(
        iam.getCurrentWorkspace,
        shell.createSnackbarRetryWrapper,
        internal.termsOfUseStore,
      ),
      new Section7216AcceptancesListViewModel(
        iam.getCurrentWorkspace,
        shell.createSnackbarRetryWrapper,
        internal.section7216Store,
      ),
    )
  })

  const profileVm = useScopedViewModel(
    ({ iam, shell }) =>
      new EditProfileViewModel(
        iam.getCurrentUser,
        iam.getCurrentMember,
        iam.workspaceMemberStore,
        shell.newSnackbar,
      ),
  )

  useDocumentTitle('Personal information')
  useActivation(vm)

  return (
    <>
      <PersonalInfoView>
        <ProfilePanelContainer vm={vm} onProfileEdit={profileVm.open} />

        <TermsOfUseAgreementsSectionContainer
          vm={vm}
          headlineId={termsHeadlineId}
        />

        {vm.workspace.config.section7216.enabled && (
          <Section7216AuthorizationsSectionContainer
            vm={vm}
            headlineId={section7216HeadlineId}
          />
        )}

        <DeleteAccountButton />
      </PersonalInfoView>

      <EditProfileDialogContainer vm={profileVm} />
    </>
  )
})

interface ProfilePanelContainerProps {
  vm: PersonalInfoViewModel
  onProfileEdit: () => unknown
}

export const ProfilePanelContainer = observer(function ProfilePanelContainer({
  vm,
  onProfileEdit,
}: ProfilePanelContainerProps) {
  return (
    <ProfilePanel
      avatarUrl={vm.user.avatarUrl ?? undefined}
      displayName={vm.user.displayName}
      email={vm.user.email}
      phone={vm.user.phone}
      isAvatarChangePending={vm.changeAvatar.pending}
      onEditProfilePress={onProfileEdit}
      onAvatarChange={(file) => void vm.changeAvatar(file)}
    />
  )
})

interface Section7216AuthorizationsSectionContainerProps {
  vm: PersonalInfoViewModel
  headlineId: string
}

const Section7216AuthorizationsSectionContainer = observer(
  function Section7216AuthorizationsSectionContainer({
    vm,
    headlineId,
  }: Section7216AuthorizationsSectionContainerProps) {
    const isSkeleton = !vm.section7216ListVm.scroller.fetch.resolved
    return (
      <Section7216AuthorizationsSection isSkeleton={isSkeleton} id={headlineId}>
        <Section7216AcceptancesListContainer
          vm={vm.section7216ListVm}
          aria-labelledby={headlineId}
        />
      </Section7216AuthorizationsSection>
    )
  },
)

interface TermsOfUseAgreementsSectionContainerProps {
  vm: PersonalInfoViewModel
  headlineId: string
}

function DeleteAccountButton() {
  const { shell } = useRootStore()

  const handlePress = useCallback(() => {
    const handle = shell.getExternalUrlDelegate().createHandle()
    handle.open(
      'https://na1.hs-data-privacy.com/request/sqZc5xrbialFCzZJcXH13Q',
    )
  }, [shell])

  return (
    <div>
      <CardButton onPress={handlePress}>
        <CardItemContent isInteractive>
          <CardTitle>Delete account</CardTitle>
          <CardSubtitle variant="sm-description">
            Request permanent account deletion
          </CardSubtitle>
        </CardItemContent>
      </CardButton>
    </div>
  )
}

export const TermsOfUseAgreementsSectionContainer = observer(
  function TermsOfUseAgreementsSectionContainer({
    vm,
    headlineId,
  }: TermsOfUseAgreementsSectionContainerProps) {
    const isSkeleton =
      !vm.termsAcceptancesListVm.termsAcceptancesScroller.fetch.resolved
    return (
      <TermsOfUseAgreementsSection isSkeleton={isSkeleton} id={headlineId}>
        <TermsAcceptancesListContainer
          vm={vm.termsAcceptancesListVm}
          aria-labelledby={headlineId}
        />
      </TermsOfUseAgreementsSection>
    )
  },
)
